import React from "react";
import styled from "styled-components";

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 25px 15px;
  margin-bottom: 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
  background-color: white;
  text-align: center;
  cursor: pointer;
  width: 100%; /* 부모 컬럼의 너비를 100% 사용 */
  max-width: 200px; /* 예시: 카드의 최대 너비를 200px로 고정 */
  min-width: 180px; /* 예시: 카드의 최소 너비를 180px로 고정 (필요에 따라) */
  position: relative;

  &:hover {
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.12);
  }
`;

const ProductImage = styled.img`
  max-width: 80%;
  height: 150px;
  object-fit: contain;
  margin-bottom: 20px;
`;

const ProductName = styled.h3`
  font-size: 1.1em;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
`;

// Repositioned and restyled Category Tag for subtlety
const CategoryDisplay = styled.div`
  position: absolute;
  top: 8px; // Slightly adjusted position
  left: 8px; // Slightly adjusted position
  background-color: rgba(0, 0, 0, 0.35); // More transparent background
  color: #f0f0f0; // Lighter text color for less contrast
  padding: 3px 6px; // Slightly smaller padding
  border-radius: 3px; // Slightly smaller border-radius
  font-size: 0.7em; // Even smaller font for subtlety
  font-weight: normal;
  z-index: 1;
  display: inline-block;
`;

const PriceContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LowestPriceHighlight = styled.div`
  font-size: 1.2em;
  font-weight: bold;
  color: #d32f2f;
  margin-bottom: 15px;
`;

const ShopPriceList = styled.div`
  display: flex;
  flex-direction: column;
  width: fit-content;
  margin: 0 auto;
  padding: 0 5px;
  box-sizing: border-box;
`;

const ShopPriceItem = styled.div`
  display: flex;
  width: 100%;
  font-size: 0.95em;
  color: #555;
  margin-bottom: 5px;
  line-height: 1.4;

  .shop-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-right: auto;
    padding-right: 5px;
    max-width: 60px;
  }

  .shop-price {
    font-weight: bold;
    color: #000;
    white-space: nowrap;
    min-width: 60px; /* 예시: 가격 부분이 차지할 최소 너비 설정 */
    text-align: right; /* 가격을 오른쪽으로 정렬 */
    display: inline-block; /* text-align 적용을 위해 */
  }
`;

const CreatedDate = styled.div`
  font-size: 0.8em;
  color: #aaa;
  margin-top: 20px;
`;

// 가격 표시 포맷 함수 (USD)
const formatPrice = (price) => {
  if (price === null || price === undefined || price === 0) {
    return "-";
  }
  // 숫자를 소수점 둘째 자리까지 반올림하여 표시
  return `$${price}`;
};

// 날짜 포맷 함수
const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("ko-KR"); // 한국 날짜 형식
};

// 면세점 이름 매핑 (짧은 버전으로 수정)
const shopNameMap = {
  lotte: "롯데",
  shilla: "신라",
  hd: "현대",
  ssg: "신세계",
};

export const AiSearchResultCard = ({ item }) => {
  if (!item) {
    return null;
  }

  // 면세점별 가격 정보를 배열로 만들고 유효한 가격만 필터링
  const shopPrices = [
    {
      name: shopNameMap.lotte,
      price: item.lotte_discount_price_usd,
      key: "lotte",
    },
    {
      name: shopNameMap.shilla,
      price: item.shilla_discount_price_usd,
      key: "shilla",
    },
    { name: shopNameMap.hd, price: item.hd_discount_price_usd, key: "hd" },
    { name: shopNameMap.ssg, price: item.ssg_discount_price_usd, key: "ssg" },
  ].filter(
    (shop) => shop.price !== null && shop.price !== undefined && shop.price > 0
  ); // 유효한 가격만

  // 가격 기준으로 오름차순 정렬
  shopPrices.sort((a, b) => a.price - b.price);

  const handleCardClick = () => {
    const productName = encodeURIComponent(item.standardized_name || "");
    let urlToOpen = "";

    if (item.official_url) {
      window.open(item.official_url, "_blank");
      return;
    }

    if (shopPrices.length > 0 && productName) {
      const primaryShopKey = shopPrices[0].key;

      if (primaryShopKey === "shilla") {
        urlToOpen = `https://www.shilladfs.com/estore/kr/ko/search?text=${productName}`;
      } else if (primaryShopKey === "lotte") {
        urlToOpen = `https://kor.lottedfs.com/kr/search?comSearchWord=${productName}`;
      } else if (primaryShopKey === "hd") {
        urlToOpen = `https://www.hddfs.com/shop/sr/searchResult.do?searchTerm=${productName}`;
      } else if (primaryShopKey === "ssg") {
        urlToOpen = `https://www.ssgdfs.com/kr/search/resultsTotal?startCount=0&offShop=&suggestReSearchReq=true&orReSearchReq=true&query=${productName}`;
      }
    }

    if (urlToOpen) {
      window.open(urlToOpen, "_blank");
    } else {
      console.log(
        "해당 상품의 링크를 생성할 수 없습니다. 상품명: ",
        item.standardized_name
      );
    }
  };

  return (
    <CardContainer onClick={handleCardClick}>
      {/* Conditionally render CategoryDisplay only if item.category exists */}
      {item.category && <CategoryDisplay>{item.category}</CategoryDisplay>}

      {item.representative_image_url && (
        <ProductImage
          src={item.representative_image_url}
          alt={item.standardized_name || "상품 이미지"}
        />
      )}

      <ProductName>{item.standardized_name || "이름 정보 없음"}</ProductName>

      <PriceContainer>
        {/* 최저가 강조 표시 */}
        {item.lowest_price !== null &&
          item.lowest_price !== undefined &&
          item.lowest_price > 0 && (
            <LowestPriceHighlight>
              최저가 ${item.lowest_price}
            </LowestPriceHighlight>
          )}

        {/* 면세점별 가격 낮은 순으로 수직 배열 */}
        <ShopPriceList>
          {shopPrices.map((shop) => (
            <ShopPriceItem key={shop.key}>
              <span className="shop-name" title={shop.name}>
                {shop.name}
              </span>
              <span className="shop-price">${shop.price}</span>
            </ShopPriceItem>
          ))}
          {shopPrices.length === 0 && (
            <ShopPriceItem>
              <span className="shop-name">면세점 가격 정보 없음</span>
            </ShopPriceItem>
          )}
        </ShopPriceList>
      </PriceContainer>

      <CreatedDate>검색일: {formatDate(item.created_at)}</CreatedDate>
    </CardContainer>
  );
};
